import { Player } from '@lottiefiles/react-lottie-player';
import { motion, useReducedMotion } from 'motion/react';
import { useState, useEffect, useCallback, useRef } from 'react';

import DefaultLink from 'components/common/DefaultLink';
import * as Routes from 'helpers/routes';
import familyLogoAnimation from 'images/familyHome/family-logo-animation.json';
import familySpaghettiHero from 'images/familyHome/family-spaghetti-hero.jpg';
import { STANDARD } from 'lib/constants';
import { logEvent, events } from 'services/analytics';

const PARENT_VARIENT = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const createSlideInVariants = prefersReducedMotion => ({
  hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 100 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      type: prefersReducedMotion ? 'tween' : 'spring',
    },
  },
});

const createImageVariants = prefersReducedMotion => ({
  initial: {
    aspectRatio: 4 / 3,
    width: '75%',
    maxWidth: '64rem',
  },
  transformed: {
    aspectRatio: 21 / 9,
    width: '100%',
    maxWidth: '80rem',
    transition: {
      duration: prefersReducedMotion ? 0 : 0.8,
      ease: 'easeInOut',
    },
  },
});

const Billboard = ({ data, onBillboardAnimationComplete }) => {
  const { heroTitle, heroSubtitle, ctaButton } = data;
  const [animateInBillboard, setAnimateInBillboard] = useState(false);
  const [billboardScrollTrigger, setBillboardScrollTrigger] = useState(false);
  const [staggerAnimationDone, setStaggerAnimationDone] = useState(false);
  const [imageAnimationDone, setImageAnimationDone] = useState(false);
  const titleRef = useRef(null);

  const prefersReducedMotion = useReducedMotion();

  const slideInVariants = createSlideInVariants(prefersReducedMotion);
  const imageVariants = createImageVariants(
    prefersReducedMotion,
    billboardScrollTrigger && staggerAnimationDone
  );

  const renderTitleWithStyledLastWord = title => {
    const words = title.split(' ');
    const lastWord = words.pop();
    const restOfText = words.join(' ');

    return (
      <>
        {restOfText}{' '}
        <span className="italic underline decoration-green-60 decoration-2 underline-offset-8">
          {lastWord}
        </span>
      </>
    );
  };

  useEffect(() => {
    const fallbackTimer = setTimeout(
      () => {
        setAnimateInBillboard(true);
      },
      prefersReducedMotion ? 0 : 3000
    );

    return () => clearTimeout(fallbackTimer);
  }, [prefersReducedMotion]);

  useEffect(() => {
    const handleScroll = () => {
      if (!billboardScrollTrigger && window.scrollY >= 100) {
        setBillboardScrollTrigger(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [billboardScrollTrigger]);

  const handleLottieEvent = useCallback(event => {
    if (event === 'complete') {
      setAnimateInBillboard(true);
    }
  }, []);

  useEffect(() => {
    if (imageAnimationDone && staggerAnimationDone) {
      onBillboardAnimationComplete();
    }
  }, [staggerAnimationDone, imageAnimationDone, onBillboardAnimationComplete]);

  return (
    <section className="relative h-full bg-green-140 px-4 py-14 bp1:px-8">
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {animateInBillboard ? 'Main content is now available' : ''}
      </div>
      {!animateInBillboard && !prefersReducedMotion && (
        <Player
          autoplay
          loop={false}
          src={familyLogoAnimation}
          onEvent={handleLottieEvent}
          className="absolute left-1/2 top-20 w-2/3 max-w-lg -translate-x-1/2 transform"
          aria-label="Animation of the Family Menu logo"
          aria-live="polite"
        />
      )}
      <motion.div
        className="flex flex-col items-center"
        variants={PARENT_VARIENT}
        initial="hidden"
        animate={animateInBillboard ? 'visible' : 'hidden'}
        onAnimationComplete={() => {
          setStaggerAnimationDone(true);
        }}
      >
        <motion.h1
          ref={titleRef}
          className="h1 bp3:display-text mb-6 max-w-4xl text-center text-white-110 bp3:mb-10"
          style={{ textWrap: 'balance' }}
          variants={slideInVariants}
        >
          {renderTitleWithStyledLastWord(heroTitle)}
        </motion.h1>
        <motion.h2
          className="text bp1:h5 mb-6 text-center text-white-110 bp3:mb-16"
          variants={slideInVariants}
        >
          {heroSubtitle}
        </motion.h2>
        <motion.div variants={slideInVariants}>
          <DefaultLink
            to={`${Routes.getBrandRoute({ brand: STANDARD, isFamily: true }).intro}?cta=family`}
            isExternal={true}
            variation="Primary"
            className="btn btnLg mb-14 flex border-none !bg-green-60 !text-green-140 hover:brightness-90 bp3:mb-24"
            onClick={() =>
              logEvent(events.familyHome.submitted, {
                urlPath: Routes.getBrandRoute({ brand: STANDARD, isFamily: true }).intro,
              })
            }
          >
            {ctaButton}
          </DefaultLink>
        </motion.div>
        <motion.div variants={slideInVariants} className="flex aspect-[21/9] w-full justify-center">
          <motion.img
            src={familySpaghettiHero}
            alt="Family enjoying spaghetti dinner together"
            className="h-auto rounded-2xl object-cover"
            variants={imageVariants}
            initial="initial"
            animate={
              prefersReducedMotion || (billboardScrollTrigger && staggerAnimationDone)
                ? 'transformed'
                : 'initial'
            }
            onAnimationComplete={() => {
              setImageAnimationDone(true);
            }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

Billboard.propTypes = {
  data: PropTypes.shape({
    heroTitle: PropTypes.string.isRequired,
    heroSubtitle: PropTypes.string.isRequired,
    ctaButton: PropTypes.string.isRequired,
  }).isRequired,
  onBillboardAnimationComplete: PropTypes.func,
};

export default Billboard;
